import { useId, useMemo, useState } from 'react';
import {
  fieldValue,
  isProtected,
  isStale,
  periodEnding,
  releaseQueue,
  releaseReport,
  releasedBetween,
  today,
  type AnyEvent,
  type Manifest,
  type Money,
  type ReleaseKind,
} from '@intentionalist/shared';
import { clearRelease, recordRelease } from './mutations.js';
import { formatAmount, parseMoney } from './money.js';
import { currencySuffix } from './currency-display.js';

/**
 * The release pipeline (Epic 7).
 *
 * Deciding to let something go and actually letting it go are different acts,
 * and the gap between them is where good intentions quietly die. This screen is
 * about that gap: what has been waiting, for how long, and what has actually
 * left.
 */
const OUTCOMES: ReleaseKind[] = ['donated', 'sold', 'gifted', 'recycled', 'discarded', 'returned'];

const STALE_OPTIONS = [
  { days: 14, label: 'a fortnight' },
  { days: 30, label: 'a month' },
  { days: 90, label: 'three months' },
];

export function ReleasePipeline({
  manifest,
  version,
  onWrite,
}: {
  manifest: Manifest;
  version: number;
  onWrite: (events: AnyEvent[]) => void;
}) {
  const [staleAfter, setStaleAfter] = useState(30);
  const [periodDays, setPeriodDays] = useState(365);
  const [recording, setRecording] = useState<string | null>(null);
  const ids = { stale: useId(), period: useId(), waiting: useId(), gone: useId() };

  const queue = useMemo(() => releaseQueue(manifest), [manifest, version]);
  const period = useMemo(() => periodEnding(periodDays), [periodDays]);
  const report = useMemo(
    () => releaseReport(manifest, period.from, period.to),
    [manifest, version, period],
  );
  const gone = useMemo(
    () => releasedBetween(manifest, period.from, period.to),
    [manifest, version, period],
  );

  return (
    <div className="review">
      <h2>Release</h2>

      <div className="field">
        <label htmlFor={ids.stale}>Flag anything waiting longer than</label>
        {/* FR-7.7: the threshold is the user's judgment, not a number we chose
            on their behalf. */}
        <select
          id={ids.stale}
          value={staleAfter}
          onChange={(e) => setStaleAfter(Number(e.target.value))}
        >
          {STALE_OPTIONS.map((option) => (
            <option key={option.days} value={option.days}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/*
        Two sections: what is still waiting, and what has already gone.
        They were one column of headings and controls with nothing between them,
        so the threshold at the top appeared to belong to the summary at the
        bottom as much as to the queue it actually filters.
      */}
      <section aria-labelledby={ids.waiting}>
        <h3 id={ids.waiting}>Waiting to go ({queue.length})</h3>

        {queue.length === 0 ? (
          <p className="muted">Nothing is waiting. Items marked Release appear here.</p>
        ) : (
          <ul className="catalog" aria-label="Release queue">
            {queue.map((entry) => {
              const name = String(fieldValue(entry.item, 'name') ?? '(unnamed)');
              const stale = isStale(entry, staleAfter);
              return (
                <li key={entry.item.itemId}>
                  <div className="catalog-row">
                    <div>
                      <strong>{name}</strong>
                      {isProtected(entry.item) && <span className="badge">protected</span>}
                      <div className={stale ? 'warn' : 'muted'}>
                        {entry.daysWaiting === null
                          ? 'waiting'
                          : `waiting ${entry.daysWaiting} day${entry.daysWaiting === 1 ? '' : 's'}`}
                      </div>
                    </div>
                    <div className="actions">
                      <button
                        type="button"
                        onClick={() =>
                          setRecording(recording === entry.item.itemId ? null : entry.item.itemId)
                        }
                      >
                        {recording === entry.item.itemId ? 'Cancel' : 'It has gone'}
                      </button>
                    </div>
                  </div>

                  {recording === entry.item.itemId && (
                    <RecordOutcome
                      itemId={entry.item.itemId}
                      name={name}
                      guarded={isProtected(entry.item)}
                      onDone={(events) => {
                        onWrite(events);
                        setRecording(null);
                      }}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section aria-labelledby={ids.gone}>
        <h3 id={ids.gone}>What has left</h3>

        <div className="field">
          <label htmlFor={ids.period}>Over the last</label>
          <select
            id={ids.period}
            value={periodDays}
            onChange={(e) => setPeriodDays(Number(e.target.value))}
          >
            <option value="30">month</option>
            <option value="90">three months</option>
            <option value="365">year</option>
          </select>
        </div>

        <dl className="sync-state">
          <dt>Items released</dt>
          <dd>{report.count}</dd>

          <dt>Proceeds</dt>
          <dd>
            {report.proceeds.length === 0 ? (
              <span className="muted">—</span>
            ) : (
              <Totals totals={report.proceeds} />
            )}
          </dd>

          <dt>Originally paid</dt>
          {/* Reported beside the proceeds and never subtracted from them: the
            difference is what owning the things cost, which is a different
            statement (NFR-SAFE-1). */}
          <dd>
            {report.purchaseValue.length === 0 ? (
              <span className="muted">—</span>
            ) : (
              <Totals totals={report.purchaseValue} />
            )}
          </dd>
        </dl>

        {gone.length > 0 && (
          <ul className="catalog" aria-label="Released items">
            {gone.map(({ item, release }) => (
              <li key={item.itemId}>
                <div className="catalog-row">
                  <div>
                    <strong>{String(fieldValue(item, 'name') ?? '(unnamed)')}</strong>
                    <div className="muted">
                      {release.outcome} · {release.onDate}
                      {release.recipient !== undefined && ` · ${release.recipient}`}
                      {release.proceeds !== undefined && ` · ${formatAmount(release.proceeds)}`}
                    </div>
                  </div>
                  <div className="actions">
                    {/* FR-7.5. The outcome stays in the log, so restoring forgets
                      nothing -- it only stops the item being archived. */}
                    <button type="button" onClick={() => onWrite(clearRelease(item.itemId))}>
                      Restore
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

const Totals = ({ totals }: { totals: Money[] }) => (
  <>
    {totals.map((total, index) => (
      <span key={total.currency}>
        {index > 0 && ' · '}
        {formatAmount(total, { ambiguous: totals.length > 1 })}
      </span>
    ))}
  </>
);

function RecordOutcome({
  itemId,
  name,
  guarded,
  onDone,
}: {
  itemId: string;
  name: string;
  /** NFR-SAFE-3: a protected item is confirmed individually before it goes. */
  guarded: boolean;
  onDone: (events: AnyEvent[]) => void;
}) {
  const ids = { outcome: useId(), date: useId(), who: useId(), money: useId(), note: useId() };
  const [outcome, setOutcome] = useState<ReleaseKind>('donated');
  const [onDate, setOnDate] = useState(today());
  const [recipient, setRecipient] = useState('');
  const [proceeds, setProceeds] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const submit = () => {
    let amount: Money | null = null;
    if (proceeds.trim() !== '') {
      amount = parseMoney(proceeds);
      if (amount === null) {
        setError('That amount is not a number. Try something like 75.00.');
        return;
      }
    }

    /*
     * NFR-SAFE-3: a protected item is asked about individually.
     *
     * Recording an outcome is the step that archives the item, so it is the
     * destructive one -- the Release verdict before it is reversible and says
     * nothing has happened yet. The second press is deliberately not a modal:
     * the form stays filled in, so confirming costs a tap and declining costs
     * nothing.
     */
    if (guarded && !confirming) {
      setConfirming(true);
      return;
    }

    onDone(recordRelease(itemId, outcome, onDate, { recipient, proceeds: amount, note }));
  };

  return (
    <div className="grid-fields">
      <div className="field">
        <label htmlFor={ids.outcome}>How did it go?</label>
        <select
          id={ids.outcome}
          value={outcome}
          onChange={(e) => setOutcome(e.target.value as ReleaseKind)}
        >
          {OUTCOMES.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor={ids.date}>On</label>
        <input
          id={ids.date}
          type="date"
          value={onDate}
          onChange={(e) => setOnDate(e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor={ids.who}>To whom</label>
        <input
          id={ids.who}
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          autoComplete="off"
        />
      </div>

      {/* Only meaningful for a sale, but not hidden for the others: a gift can
          come with money, and guessing would be presumptuous. */}
      <div className="field">
        <label htmlFor={ids.money}>
          Proceeds
          {currencySuffix()}
        </label>
        <input
          id={ids.money}
          value={proceeds}
          onChange={(e) => setProceeds(e.target.value)}
          inputMode="decimal"
          placeholder="75.00"
        />
      </div>

      <div className="field">
        <label htmlFor={ids.note}>Note</label>
        <input id={ids.note} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>

      <div className="field">
        <p className="warn" role="alert" aria-live="polite">
          {error ?? ''}
        </p>
        {confirming && (
          <p className="warn" role="alert">
            <strong>{name}</strong> is marked protected. Recording this archives it.
          </p>
        )}
        <button type="button" className="primary" onClick={submit}>
          {confirming ? 'Yes, it has gone' : 'Record'}
        </button>
      </div>
    </div>
  );
}
